import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";
import { useAuthStore } from "../../store/authStore";
import { useToast } from "../../hooks/useToast";
import { screenshotService } from "../../services/screenshotService";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: () => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    url: "",
    guest_password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const { showToast } = useToast();
  const user = useAuthStore((state) => state.user);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const getWidgetSnippet = (projectId: string) => {
    return `<!-- FeedbackFlow Widget -->
<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
<script src="https://feedbackflownew.netlify.app/widget/widget.iife.js"></script>
<link rel="stylesheet" href="https://feedbackflownew.netlify.app/style.css">

<script>
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      if (!window.FeedbackFlowWidget) {
        throw new Error('FeedbackFlowWidget failed to load');
      }
      await window.FeedbackFlowWidget.init({
        projectId: "${projectId}"
      });
      console.log('Widget initialized successfully');
    } catch (error) {
      console.error('Widget initialization failed:', error);
    }
  });
</script>`;
  };

  const checkPasswordUniqueness = async (password: string) => {
    const { data, error } = await supabase
      .from("projects")
      .select("id")
      .eq("guest_password", password)
      .single();

    if (data) {
      throw new Error(
        "This password is already in use. Please choose a different password."
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Check subscription status
    const { data: subscription } = await supabase
      .from("subscriptions")
      .select("status")
      .eq("user_id", user.id)
      .single();

    if (!subscription || subscription.status !== "active") {
      showToast("Please subscribe to create projects", "error");
      onClose();
      // Open subscription modal instead of redirecting
      setIsSubscriptionModalOpen(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if password is unique
      await checkPasswordUniqueness(formData.guest_password);

      // Try to get screenshot, use placeholder if it fails
      let screenshotUrl;
      try {
        screenshotUrl = await screenshotService.captureWebsiteScreenshot(
          formData.url
        );
      } catch (err) {
        console.error("Screenshot error:", err);
        screenshotUrl = "/images/placeholder-screenshot.png";
      }

      const { data, error: createError } = await supabase
        .from("projects")
        .insert([
          {
            name: formData.name,
            description: formData.description,
            url: formData.url,
            guest_password: formData.guest_password,
            owner_id: user.id,
            screenshot_url: screenshotUrl,
          },
        ])
        .select()
        .single();

      if (createError) throw createError;

      showToast("Project created successfully!", "success");
      onProjectCreated();
      onClose();

      // Redirect to widget URL
      window.location.href = `/?mode=widget&project=${formData.guest_password}`;
    } catch (err) {
      console.error("Error creating project:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create project. Please try again."
      );
      showToast("Failed to create project. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyWidgetCode = () => {
    const widgetCode = getWidgetSnippet(formData.guest_password);
    navigator.clipboard.writeText(widgetCode);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Create New Project
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-red-800">
                            {error}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Project Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="url"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Website URL
                    </label>
                    <input
                      type="url"
                      id="url"
                      value={formData.url}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="guest_password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Project Password
                    </label>
                    <input
                      type="text"
                      id="guest_password"
                      value={formData.guest_password}
                      onChange={handleChange}
                      placeholder="Enter a password for guest access"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This password will be used for guest access to your
                      project
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Widget Code
                    </label>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <pre className="text-sm overflow-y-auto max-h-[120px] whitespace-pre scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                        {getWidgetSnippet(formData.guest_password)}
                      </pre>
                      <button
                        type="button"
                        onClick={copyWidgetCode}
                        className="mt-2 flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        {copySuccess ? "Copied!" : "Copy Code"}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {isLoading ? "Creating..." : "Create Project"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
